import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calculator, Brain, Trophy, Grid3X3, Atom, Sigma, MessageCircle, Mail, Users } from "lucide-react";
import MultiplicationTable from "@/components/MultiplicationTable";
import MultiplicationCards from "@/components/MultiplicationCards";
import MultiplicationCalculator from "@/components/MultiplicationCalculator";
import MethodSimulation from "@/components/MethodSimulation";
import MemoryMode from "@/components/MemoryMode";
import QuickTest from "@/components/QuickTest";
import ChallengeMode from "@/components/ChallengeMode";
import MatrixMultiplication from "@/components/MatrixMultiplication";
import ComplexMultiplication from "@/components/ComplexMultiplication";
import AlgebraicMultiplication from "@/components/AlgebraicMultiplication";
import AIChat from "@/components/AIChat";
import FloatingAIChat from "@/components/FloatingAIChat";
import WelcomePopup from "@/components/WelcomePopup";

const Index = () => {
  const [activeTab, setActiveTab] = useState("basic");

  return (
    <div className="min-h-screen bg-gradient-to-br from-futuristic-1/10 via-background to-futuristic-2/10">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-md border-b border-gray-200 shadow-sm">
  <div className="container mx-auto px-4 py-2">
    <div className="text-center">
      <div className="flex items-center justify-center gap-3 mb-1">
        <div className="rounded-full bg-gradient-to-tr from-blue-400 to-purple-500 p-2 shadow-md">
          <Calculator className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-3xl font-extrabold bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 text-transparent bg-clip-text drop-shadow-sm">
          Perkalian.com
        </h1>
        <div className="rounded-full bg-gradient-to-tr from-pink-400 to-yellow-400 p-2 shadow-md">
          <Grid3X3 className="w-6 h-6 text-white" />
        </div>
      </div>
      <p className="text-gray-600 mt-1 text-base">
        Platform Pembelajaran Perkalian Lengkap & Interaktif
      </p>
    </div>
  </div>
</header>



      {/* Main Content */}
      <main className="container mx-auto px-4 pt-0 pb-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:grid-cols-8 gap-2 h-auto p-2 bg-gray-50/50 rounded-xl">
            <TabsTrigger 
              value="basic" 
              className="flex flex-col gap-1 h-16 transition-all duration-300 hover:bg-gray-100 data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-400/90 data-[state=active]:to-indigo-500/90 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Calculator className="w-4 h-4" />
              <span className="text-xs">Dasar</span>
            </TabsTrigger>
            <TabsTrigger 
              value="memory" 
              className="flex flex-col gap-1 h-16 transition-all duration-300 hover:bg-gray-100 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-400/90 data-[state=active]:to-teal-500/90 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Brain className="w-4 h-4" />
              <span className="text-xs">Hafalan</span>
            </TabsTrigger>
            <TabsTrigger 
              value="test" 
              className="flex flex-col gap-1 h-16 transition-all duration-300 hover:bg-gray-100 data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-400/90 data-[state=active]:to-orange-400/90 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Trophy className="w-4 h-4" />
              <span className="text-xs">Uji Cepat</span>
            </TabsTrigger>
            <TabsTrigger 
              value="challenge" 
              className="flex flex-col gap-1 h-16 transition-all duration-300 hover:bg-gray-100 data-[state=active]:bg-gradient-to-br data-[state=active]:from-rose-400/90 data-[state=active]:to-pink-500/90 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Trophy className="w-4 h-4" />
              <span className="text-xs">Tantangan</span>
            </TabsTrigger>
            <TabsTrigger 
              value="matrix" 
              className="flex flex-col gap-1 h-16 transition-all duration-300 hover:bg-gray-100 data-[state=active]:bg-gradient-to-br data-[state=active]:from-violet-400/90 data-[state=active]:to-purple-500/90 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Grid3X3 className="w-4 h-4" />
              <span className="text-xs">Matriks</span>
            </TabsTrigger>
            <TabsTrigger 
              value="complex" 
              className="flex flex-col gap-1 h-16 transition-all duration-300 hover:bg-gray-100 data-[state=active]:bg-gradient-to-br data-[state=active]:from-fuchsia-400/90 data-[state=active]:to-purple-500/90 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Atom className="w-4 h-4" />
              <span className="text-xs">Kompleks</span>
            </TabsTrigger>
            <TabsTrigger 
              value="algebra" 
              className="flex flex-col gap-1 h-16 transition-all duration-300 hover:bg-gray-100 data-[state=active]:bg-gradient-to-br data-[state=active]:from-cyan-400/90 data-[state=active]:to-blue-500/90 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Sigma className="w-4 h-4" />
              <span className="text-xs">Aljabar</span>
            </TabsTrigger>
            <TabsTrigger 
              value="ai" 
              className="flex flex-col gap-1 h-16 transition-all duration-300 hover:bg-gray-100 data-[state=active]:bg-gradient-to-br data-[state=active]:from-indigo-400/90 data-[state=active]:to-blue-500/90 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs">Tanya AI</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="mt-6 space-y-6">
            {/* Card 1: Tabel Perkalian (MultiplicationTable) */}
            <Card>
              <CardHeader>
                <CardTitle>Tabel Perkalian</CardTitle>
                <CardDescription>
                  Jelajahi tabel perkalian interaktif
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MultiplicationTable />
              </CardContent>
            </Card>
            
            {/* Card 2: Kalkulator Perkalian (MultiplicationCalculator) */}
            <Card>
              <CardHeader>
                <CardTitle>Kalkulator Perkalian</CardTitle>
                <CardDescription>
                  Hitung perkalian dengan langkah-langkah detail
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MultiplicationCalculator />
              </CardContent>
            </Card>

            {/* Card 3: Kartu Perkalian (MultiplicationCards) */}
            <Card>
              <CardHeader>
                <CardTitle>Kartu Perkalian</CardTitle>
                <CardDescription>
                  Pelajari perkalian dengan kartu interaktif
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MultiplicationCards />
              </CardContent>
            </Card>

            {/* Card 3: Simulasi Metode Perkalian (MethodSimulation) */}
            <Card>
              <CardHeader>
                <CardTitle>Simulasi Metode Perkalian</CardTitle>
                <CardDescription>
                  Pelajari berbagai metode perkalian dengan visualisasi
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MethodSimulation />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="memory" className="mt-6">
            <MemoryMode />
          </TabsContent>

          <TabsContent value="test" className="mt-6">
            <QuickTest />
          </TabsContent>

          <TabsContent value="challenge" className="mt-6">
            <ChallengeMode />
          </TabsContent>

          <TabsContent value="matrix" className="mt-6">
            <MatrixMultiplication />
          </TabsContent>

          <TabsContent value="complex" className="mt-6">
            <ComplexMultiplication />
          </TabsContent>

          <TabsContent value="algebra" className="mt-6">
            <AlgebraicMultiplication />
          </TabsContent>

          <TabsContent value="ai" className="mt-6">
            <AIChat />
          </TabsContent>

          {/* Hero Section - Only in Dasar tab */}
          <TabsContent value="basic" className="mt-6">
           
          </TabsContent>
        </Tabs>

        {/* About Us Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-purple-50/50 to-pink-50/50 -z-10"></div>
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-200/30 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute -bottom-12 -left-12 w-96 h-96 bg-purple-200/30 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-pink-200/30 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
          
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 text-sm font-medium text-blue-600 bg-blue-100 rounded-full mb-4">
                Tentang Platform Kami
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Belajar Perkalian Menjadi Menyenangkan
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto rounded-full"></div>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-blue-100/40 overflow-hidden border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="p-8 sm:p-10 lg:p-12">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl">
                        <Users className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        Tentang Kami
                      </h3>
                    </div>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      Perkalian.com adalah platform pembelajaran matematika revolusioner yang dirancang untuk membuat belajar perkalian menjadi pengalaman yang menyenangkan dan interaktif. Dengan teknologi canggih dan pendekatan yang inovatif, kami membantu siswa menguasai konsep perkalian dengan mudah.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 text-sm font-bold">✓</span>
                          </div>
                        </div>
                        <p className="text-gray-600">
                          <span className="font-medium text-gray-800">Pembelajaran Interaktif</span> - Metode belajar yang menarik dengan visualisasi dan animasi
                        </p>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="text-purple-600 text-sm font-bold">✓</span>
                          </div>
                        </div>
                        <p className="text-gray-600">
                          <span className="font-medium text-gray-800">Teknologi Canggih</span> - Dukungan Text-to-Speech dan AI untuk pengalaman belajar optimal
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-8 sm:p-10 lg:p-12 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full -ml-32 -mb-32"></div>
                    
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                        <span className="p-2 bg-white/20 rounded-lg">✨</span>
                        Fitur Unggulan
                      </h3>
                      
                      <ul className="space-y-4">
                        {[
                          { icon: '🎵', title: 'Text-to-Speech', desc: 'Dukungan suara Bahasa Indonesia untuk memudahkan belajar' },
                          { icon: '🎮', title: 'Mode Game', desc: 'Belajar sambil bermain dengan berbagai tantangan seru' },
                          { icon: '🤖', title: 'AI Assistant', desc: 'Bantuan langsung dari asisten matematika cerdas' },
                          { icon: '📊', title: 'Laporan Belajar', desc: 'Pantau perkembangan belajar dengan analisis mendalam' },
                          { icon: '🌈', title: 'Desain Interaktif', desc: 'Antarmuka yang menarik dan mudah digunakan' }
                        ].map((feature, index) => (
                          <li key={index} className="flex gap-3 group">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-xl group-hover:bg-white/20 transition-colors">
                              {feature.icon}
                            </div>
                            <div>
                              <h4 className="font-semibold">{feature.title}</h4>
                              <p className="text-sm text-blue-100 opacity-90">{feature.desc}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                      
                      <div className="mt-8 pt-6 border-t border-white/10">
                        <p className="text-sm text-blue-100/90 mb-4">
                          Bergabunglah dengan ribuan siswa yang telah merasakan pengalaman belajar berbeda
                        </p>
                        <button className="px-6 py-2.5 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors text-sm">
                          Mulai Belajar Sekarang
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <section className="mt-2 py-6">
          <Card className="bg-gradient-to-r from-emerald-50/80 to-blue-50/80 backdrop-blur-sm border border-white/50 shadow-sm">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
                📝 Blog Pendidikan
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center px-6 pb-6 pt-0">
              <p className="text-gray-600 mb-5">
                Baca artikel menarik tentang tips dan trik belajar matematika!
              </p>
              <Link 
                to="/blog"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-blue-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                📖 Baca Blog
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* Contact Section */}
        <section className="mt-2 py-6">
          <Card className="bg-gradient-to-r from-blue-50/80 to-purple-50/80 backdrop-blur-sm border border-white/50 shadow-sm">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center justify-center gap-3">
                <Mail className="w-6 h-6 text-purple-500" />
                Hubungi Kami
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center px-6 pb-6 pt-0">
              <p className="text-gray-600 mb-5">
                Ada pertanyaan, saran, atau masukan? Jangan ragu untuk menghubungi kami!
              </p>
              <a 
                href="mailto:contact@perkalian.com"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                <Mail className="w-5 h-5" />
                contact@perkalian.com
              </a>
            </CardContent>
          </Card>
        </section>
      </main>
      
      {/* Floating AI Chat */}
      <FloatingAIChat />
      
      {/* Welcome Popup */}
      <WelcomePopup />

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-500 bg-white/50 backdrop-blur-sm border-t border-gray-100 mt-8">
        <div className="container mx-auto px-4">
          <p>
            © {new Date().getFullYear()} Perkalian.com - Platform Pembelajaran Perkalian Interaktif | 
            Design by {' '}
            <a 
              href="https://www.cateringsemarang.id" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Catering Semarang
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;